import { useEffect, useState, useCallback } from 'react';
import CacheManager from '../utils/cacheManager';

interface CacheManagerHook {
  cacheManager: CacheManager;
  isUpdateAvailable: boolean;
  clearCaches: () => Promise<void>;
  forceReload: () => void;
  bustUrl: (url: string) => string;
  getCacheHeaders: () => HeadersInit;
}

/**
 * React hook for cache management
 * Provides cache busting and update detection functionality
 */
export const useCacheManager = (): CacheManagerHook => {
  const [isUpdateAvailable, setIsUpdateAvailable] = useState(false);
  const cacheManager = CacheManager.getInstance();

  useEffect(() => {
    // Set up cache management
    cacheManager.setupAutoInvalidation();

    // Listen for update notifications
    const handleUpdateAvailable = () => {
      setIsUpdateAvailable(true);
    };

    window.addEventListener('app-update-available', handleUpdateAvailable);

    return () => {
      window.removeEventListener('app-update-available', handleUpdateAvailable);
    };
  }, [cacheManager]);

  const clearCaches = useCallback(async () => {
    await cacheManager.clearAllCaches();
    setIsUpdateAvailable(false);
  }, [cacheManager]);

  const forceReload = useCallback(() => {
    cacheManager.forceReload();
  }, [cacheManager]);

  const bustUrl = useCallback((url: string) => {
    return cacheManager.bustUrl(url);
  }, [cacheManager]);

  const getCacheHeaders = useCallback(() => {
    return cacheManager.getCacheHeaders();
  }, [cacheManager]);

  return {
    cacheManager,
    isUpdateAvailable,
    clearCaches,
    forceReload,
    bustUrl,
    getCacheHeaders
  };
};

/**
 * Hook specifically for API calls with cache busting
 */
export const useApiWithCacheBusting = () => {
  const { getCacheHeaders, bustUrl } = useCacheManager();

  const fetchWithCacheBusting = useCallback(async (
    url: string,
    options: RequestInit = {}
  ): Promise<Response> => {
    const bustedUrl = bustUrl(url);
    const headers = {
      ...getCacheHeaders(),
      ...options.headers
    };

    return fetch(bustedUrl, {
      ...options,
      headers
    });
  }, [bustUrl, getCacheHeaders]);

  return { fetchWithCacheBusting };
};